import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { GetTokenTATReport } from "../../../lib/api";
import {
  ShowLoadingAnimation,
  ShowNoDataAnimation,
} from "../../../components/common/Animation";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import Flatpickr from "react-flatpickr";
import { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";

const formatDate = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const TATReport = () => {
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isDownloadExcel, setIsDownloadExcel] = useState(false);
  const pageSize = 10;
  const startDate = dateRange[0] ? formatDate(dateRange[0]) : null;
  const endDate = dateRange[1] ? formatDate(dateRange[1]) : null;

  // Query Backend TAT Report API
  const { isLoading, isError, data } = useQuery({
    queryKey: [
      "GetTokenTATReport",
      startDate,
      endDate,
      pageNumber,
      pageSize,
      searchParam,
    ],
    queryFn: () =>
      GetTokenTATReport(
        startDate,
        endDate,
        pageNumber,
        pageSize,
        searchParam,
      ),
    keepPreviousData: true,
  });

  useEffect(() => {
    setPageNumber(1);
  }, [searchParam, startDate, endDate]);

  const reportResponse = data?.data || {};
  const reportData = reportResponse.Data || [];
  const totalPages = reportResponse.TotalPages || 1;
  const totalCount = reportResponse.TotalCount || 0;

  const stats = useMemo(() => {
    return {
      totalTime: reportResponse.TotalTime || "00:00",
      numberOfPatients: reportResponse.NumberOfPatients || 0,
      averageTAT: reportResponse.AverageTAT || "00:00",
    };
  }, [reportResponse]);

  const handlePageChange = (_, newPage) => {
    setPageNumber(newPage);
  };

  const handleDownloadExcel = async () => {
    if (isLoading) return;

    if (totalCount === 0) {
      toast.toast ? toast("No data to Download") : toast.error("No data to Download", {
        position: toast.POSITION?.TOP_RIGHT || "top-right",
      });
      return;
    }

    setIsDownloadExcel(true);

    try {
      // Fetch all records for the Excel sheet
      const dataResponse = await GetTokenTATReport(
        startDate,
        endDate,
        1,
        10000,
        searchParam,
      );

      if (dataResponse.status === 200) {
        const excelData = dataResponse?.data?.Data || [];

        if (excelData.length === 0) {
          toast.error("No data available for download");
          setIsDownloadExcel(false);
          return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("TAT Report");

        worksheet.columns = [
          { header: "Token Number", key: "tokenNumber", width: 20 },
          { header: "Date", key: "date", width: 15 },
          { header: "Time In", key: "timeIn", width: 15 },
          { header: "Time Out", key: "timeOut", width: 15 },
          { header: "TAT", key: "tat", width: 15 },
        ];

        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF1F2937" },
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        excelData.forEach((item) => {
          const prefix = item.TokenPrefix || "";
          worksheet.addRow({
            tokenNumber: prefix + (item.TokenNumber || ""),
            date: item.Date ? item.Date.split("T")[0] : "N/A",
            timeIn: item.TimeIn || "N/A",
            timeOut: item.TimeOut || "N/A",
            tat: item.TAT || "N/A",
          });
        });

        // Add summary statistics at the bottom of the sheet
        worksheet.addRow([]);
        worksheet.addRow(["TOTAL TIME", dataResponse.data.TotalTime || "00:00"]);
        worksheet.addRow(["NO OF PATIENT", dataResponse.data.NumberOfPatients || 0]);
        worksheet.addRow(["AVERAGE TAT", dataResponse.data.AverageTAT || "00:00"]);

        // Format bottom cells with yellow color
        const totalRow = worksheet.getRow(worksheet.rowCount - 2);
        const countRow = worksheet.getRow(worksheet.rowCount - 1);
        const avgRow = worksheet.getRow(worksheet.rowCount);

        [totalRow, countRow, avgRow].forEach((row) => {
          row.getCell(1).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFFEB3B" }, // Yellow accent
          };
          row.getCell(1).font = { bold: true };
          row.getCell(2).font = { bold: true };
        });

        worksheet.columns.forEach((column) => {
          column.width = column.width || 15;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const date = new Date().toISOString().split("T")[0];
        const fileName = `TAT_Report_${date}.xlsx`;

        saveAs(new Blob([buffer]), fileName);

        toast.success("Excel file downloaded successfully");
      } else {
        toast.error("Failed to fetch data for download");
      }
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Error downloading Excel file");
    } finally {
      setIsDownloadExcel(false);
    }
  };

  return (
    <div className="py-5">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-xl min-[480px]:text-2xl sm:text-3xl font-bold text-gray-800">
          TAT Report
        </h4>
        <div className="relative">
          <img
            className="cursor-pointer flex w-[60px] hover:scale-105 transition-transform"
            onClick={handleDownloadExcel}
            src="/image/element/excel.png"
            alt="excel_icon"
          />
          {isDownloadExcel && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded z-10">
              <div className="flex justify-center items-center m-auto">
                <div role="status">
                  <svg
                    aria-hidden="true"
                    className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="currentColor"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col min-[480px]:flex-row justify-between items-center gap-y-2">
        {/* Date Range Picker */}
        <div
          id="date-range-picker"
          className="flex items-center w-full min-[480px]:w-auto"
        >
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
              </svg>
            </div>
            <Flatpickr
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              options={{ mode: "range", dateFormat: "Y-m-d" }}
              className="bg-gray-50 border border-gray-300 w-full md:w-auto h-full text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Select date range"
            />
          </div>
        </div>

        {/* Token Number Search */}
        <div className="m-0 w-full min-[480px]:w-auto">
          <input
            className="border border-gray-400 rounded-[5px] px-3 py-1.5 focus:outline-none w-full min-[480px]:w-auto"
            placeholder="Search Token Number"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
          />
        </div>
      </div>

      <div className="w-full">
        <div className="mt-5 border-2 rounded-lg">
          <div className="overflow-auto">
            <TableContainer>
              <Table>
                <TableHead className="bg-gray-800 rounded-xl">
                  <TableRow>
                    <TableCell style={{ color: "white" }}>Token Number</TableCell>
                    <TableCell style={{ color: "white" }}>Date</TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Time In
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      Time Out
                    </TableCell>
                    <TableCell style={{ color: "white", textAlign: "center" }}>
                      TAT (HH:MM)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <div className="w-44 flex justify-center m-auto">
                          <ShowLoadingAnimation />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : reportData && reportData.length > 0 ? (
                    <>
                      {reportData.map((row, index) => {
                        const prefix = row.TokenPrefix || "";
                        return (
                          <TableRow key={(row.TokenNumber || index) + "_" + index}>
                            <TableCell sx={{ whiteSpace: "pre", fontWeight: "bold" }}>
                              {prefix + (row.TokenNumber || "")}
                            </TableCell>
                            <TableCell>
                              {row.Date ? row.Date.split("T")[0] : "N/A"}
                            </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            {row.TimeIn || "N/A"}
                          </TableCell>
                          <TableCell style={{ textAlign: "center" }}>
                            {row.TimeOut || "N/A"}
                          </TableCell>
                          <TableCell style={{ textAlign: "center", fontWeight: "semibold" }}>
                            {row.TAT || "N/A"}
                          </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Integrated Excel-style Summary Rows aligned with columns */}
                      <TableRow>
                        <TableCell colSpan={3} sx={{ borderBottom: "none" }} />
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f3f4f6",
                            textAlign: "left",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          TOTAL TIME
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {stats.totalTime}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ borderBottom: "none" }} />
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f3f4f6",
                            textAlign: "left",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          NO OF PATIENT
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {stats.numberOfPatients}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ borderBottom: "none" }} />
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f3f4f6",
                            textAlign: "left",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          AVERAGE TAT
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {stats.averageTAT}
                        </TableCell>
                      </TableRow>
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>

      {/* Pagination Aligned Centered */}
      {!isLoading && reportData.length > 0 && (
        <div className="flex justify-center items-center mt-6">
          <Stack spacing={2}>
            <Pagination
              count={totalPages}
              page={pageNumber}
              onChange={handlePageChange}
              color="primary"
            />
          </Stack>
        </div>
      )}

      <div className="flex justify-center">
        {isError && !reportData.length && (
          <div className="w-44 flex justify-center">
            <ShowNoDataAnimation />
          </div>
        )}
      </div>
    </div>
  );
};

export default TATReport;
